import { useState } from 'react';

import { notifySuccess } from '@/portainer/services/notifications';
import { UserId } from '@/portainer/users/types';
import { Team, TeamId, TeamRole } from '@/react/portainer/users/teams/types';

import { LoadingButton } from '@@/buttons';
import { PortainerSelect } from '@@/form-components/PortainerSelect';

import { useAddUserToTeamMutation } from './queries/useAddUserToTeamMutation';

export function AddToTeamForm({
  userId,
  teams,
}: {
  userId: UserId;
  teams: Array<Team>;
}) {
  const [teamId, setTeamId] = useState<TeamId | null>(null);
  const [role, setRole] = useState<TeamRole>(TeamRole.Member);
  const addMutation = useAddUserToTeamMutation(userId);

  if (teams.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      <div className="w-56 shrink-0">
        <PortainerSelect<TeamId | null>
          placeholder="Select a team"
          value={teamId}
          options={teams.map((team) => ({ value: team.Id, label: team.Name }))}
          onChange={setTeamId}
          size="sm"
          bindToBody
          aria-label="Team to add this user to"
          data-cy="user-add-team-select"
        />
      </div>
      <div className="w-32 shrink-0">
        <PortainerSelect<TeamRole>
          value={role}
          options={[
            { value: TeamRole.Member, label: 'Member' },
            { value: TeamRole.Leader, label: 'Leader' },
          ]}
          onChange={(value) => setRole(value ?? TeamRole.Member)}
          size="sm"
          bindToBody
          aria-label="Role in the team"
          data-cy="user-add-team-role-select"
        />
      </div>
      <LoadingButton
        loadingText="Adding"
        isLoading={addMutation.isLoading}
        disabled={!teamId}
        onClick={handleAdd}
        data-cy="user-add-team-button"
      >
        Add to team
      </LoadingButton>
    </div>
  );

  function handleAdd() {
    if (!teamId) {
      return;
    }

    addMutation.mutate(
      { teamId, role },
      {
        onSuccess() {
          notifySuccess('Success', 'User added to team');
          setTeamId(null);
          setRole(TeamRole.Member);
        },
      }
    );
  }
}
